import { AuthContext } from '@/auth/context';
import { GeneralErrorBoundary } from '@/components/general-error-boundary';
import { LoadingPage } from '@/components/loading-page';
import { Sheet, SheetClose, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { isCoachOnboarding } from '@/lib/coach-utils';
import { Button } from '@/components/ui/button';
import { useErrorLogger } from '@/hooks/use-error-logger';
import { EmptyArray } from '@/lib/api-calls';
import { DEFAULT_PAGE_SIZE } from '@/lib/pagination';
import { cn } from '@/lib/tailwind-utils';
import { MobileHeader } from '@/routes/_layout/-components/mobile-header';
import { NavLinkDefinition } from '@/routes/_layout/-components/nav-link';
import {
  SIDEBAR_COLLAPSED_ADMIN_TEACHER_KEY,
  SIDEBAR_COLLAPSED_SCHOOL_ADMIN_KEY,
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
} from '@/routes/_layout/-components/sidebar-collapse';
import { SidebarContent } from '@/routes/_layout/-components/sidebar-content';
import { NAV } from '@airreading/automation-test/test-ids';
import {
  isAirAdmin,
  isCoach,
  isCoachAllStatuses,
  isEdgeCoach,
  isFamilyStudent,
  isSchoolAdmin,
  isStudent,
} from '@airreading/common';
import { LoggedInUser, maxBookPartForStudentGrade, StudentYearSelectable } from '@airreading/common/data';
import { createFileRoute, Navigate, Outlet, useLocation, useMatchRoute } from '@tanstack/react-router';
import {
  ArrowLeft,
  Book,
  BookOpen,
  CalendarCheck,
  CircleUser,
  ClipboardCheck,
  GraduationCap,
  HeartHandshake,
  Home,
  Library,
  SquareUser,
  Users,
} from 'lucide-react';
import { CSSProperties, useContext, useEffect, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import BellIcon from './assets/bell-icon.svg?react';
import CalendarDollarIcon from './assets/calendar-dollar-icon.svg?react';
import CalendarIcon from './assets/calendar-icon.svg?react';
import SchoolsIcon from './assets/schools-icon.svg?react';
import SessionsIcon from './assets/sessions-icon.svg?react';
import StudentsIcon from './assets/students-icon.svg?react';
import TeachersIcon from './assets/teachers-icon.svg?react';

export const Route = createFileRoute('/_layout')({
  component: RootLayoutComponent,
});

const isCoachAndOnboarding = (user: LoggedInUser) => isCoach(user) && isCoachOnboarding(user);

const getNavLinks = (): NavLinkDefinition[] => [
  {
    label: 'Air Reading class',
    iconComponent: Home,
    linkProps: { to: '/' },
    shouldRender: isStudent,
  },
  {
    label: 'Handouts & feedback',
    iconComponent: ClipboardCheck,
    linkProps: { to: '/handouts-and-feedback', search: { pageSize: 30, pageIndex: 0 } },
    shouldRender: isFamilyStudent,
  },
  {
    label: 'New teacher onboarding',
    iconComponent: SquareUser,
    linkProps: { to: '/getting-ready-to-teach' },
    shouldRender: isCoachAndOnboarding,
  },
  {
    label: 'My classes',
    iconComponent: CalendarIcon,
    linkProps: { to: '/my-classes' },
    shouldRender: isCoach,
    children: [
      {
        label: 'Upcoming classes',
        iconComponent: CalendarIcon,
        linkProps: { to: '/my-classes/upcoming', search: { pageSize: 30, pageIndex: 0 } },
        shouldRender: isCoach,
      },
      {
        label: 'Past classes',
        iconComponent: CalendarIcon,
        linkProps: { to: '/my-classes/past', search: { pageSize: 30, pageIndex: 0 } },
        shouldRender: isCoach,
      },
    ],
  },
  {
    label: 'Class feedback',
    iconComponent: ClipboardCheck,
    linkProps: { to: '/teachers-feedback' },
    shouldRender: isCoach,
  },
  {
    label: 'My availability',
    iconComponent: CalendarCheck,
    linkProps: { to: '/my-availability', search: { schedule: 'regular' } },
    shouldRender: (user) => isCoach(user) && !isEdgeCoach(user),
  },
  {
    label: 'My student groups',
    iconComponent: Users,
    linkProps: { to: '/my-student-groups' },
    shouldRender: (user) => isCoach(user) && !isEdgeCoach(user),
  },
  { isDivider: true, shouldRender: isCoach },
  {
    label: 'Help center',
    iconComponent: BellIcon,
    externalURL: 'https://help.airreading.com/support/home',
    shouldRender: isCoach,
  },
  {
    label: 'Payments',
    iconComponent: CalendarDollarIcon,
    linkProps: { to: '/payments' },
    shouldRender: (user) => isCoach(user) && !isEdgeCoach(user),
  },
  {
    label: 'Academy',
    iconComponent: GraduationCap,
    externalURL: 'https://help.airreading.com/support/solutions/157000561205',
    shouldRender: isCoach,
  },
  {
    label: 'Request substitutes',
    iconComponent: HeartHandshake,
    externalURL:
      'https://help.airreading.com/support/solutions/articles/157000372881-requesting-a-substitute-in-the-air-reading-v2-teacher-portal#Regular-Sub-Request-/-Short-Notice-Sub-Request-(class-has-not-yet-started)',
    shouldRender: (user) => isCoach(user) && !isEdgeCoach(user),
  },
  { isDivider: true, shouldRender: isCoach },
  {
    label: 'Account',
    iconComponent: CircleUser,
    linkProps: { to: '/profile' },
    shouldRender: isCoachAllStatuses,
  },
  {
    label: 'Account',
    iconComponent: CircleUser,
    linkProps: { to: '/account' },
    shouldRender: isStudent,
  },
  { isDivider: true, shouldRender: isCoach },
  {
    label: 'Sessions',
    iconComponent: SessionsIcon,
    linkProps: { to: '/sessions' },
    shouldRender: isAirAdmin,
  },
  {
    label: 'Student groups',
    iconComponent: Users,
    linkProps: { to: '/student-groups' },
    shouldRender: isAirAdmin,
  },
  {
    label: 'Schools',
    iconComponent: SchoolsIcon,
    linkProps: { to: '/orgs/schools', search: { pageSize: DEFAULT_PAGE_SIZE, pageIndex: 0 } },
    shouldRender: isAirAdmin,
  },
  {
    label: 'Teachers',
    iconComponent: TeachersIcon,
    linkProps: { to: '/teachers' },
    shouldRender: isAirAdmin,
  },
  {
    label: 'Students',
    iconComponent: StudentsIcon,
    linkProps: { to: '/students' },
    shouldRender: isAirAdmin,
  },
  {
    label: 'Students',
    iconComponent: StudentsIcon,
    linkProps: { to: '/school-students' },
    shouldRender: (user) => isSchoolAdmin(user) && !isAirAdmin(user),
  },
  {
    label: 'Lessons',
    iconComponent: Library,
    linkProps: { to: '/lessons', search: { pageSize: DEFAULT_PAGE_SIZE, pageIndex: 0 } },
    shouldRender: isAirAdmin,
  },
  {
    label: 'Accounts',
    iconComponent: CircleUser,
    linkProps: { to: '/users' },
    shouldRender: isAirAdmin,
  },
];

function getMenuNavLinks(actingUser: LoggedInUser | undefined): NavLinkDefinition[] {
  const navLinks = getNavLinks();

  if (!actingUser) return navLinks;

  // School portal takes precedence over coach/student menus when the acting user is a school admin.
  if (isSchoolAdmin(actingUser) && !isAirAdmin(actingUser)) {
    return navLinks.filter((nav) => 'linkProps' in nav && nav.linkProps.to === '/school-students');
  }

  if (isStudent(actingUser) && !isFamilyStudent(actingUser)) {
    const grade = (actingUser.grade ?? 'null') as StudentYearSelectable;
    const maxBookLevel = maxBookPartForStudentGrade(grade);
    const maxBookLevelArray = Array.from({ length: maxBookLevel }, (_, i) => maxBookLevel - i);
    const booksNav: NavLinkDefinition = {
      label: 'Books',
      iconComponent: BookOpen,
      linkProps: { to: '/books' },
      shouldRender: isStudent,
      children: maxBookLevelArray.map((partId) => ({
        label: `Part ${partId}`,
        iconComponent: Book,
        linkProps: {
          to: '/books/part/$partId',
          params: { partId: String(partId) },
        },
        shouldRender: isStudent,
      })),
    };

    const handbookIndex = navLinks.findIndex((nav) => 'label' in nav && nav.label === 'My classes');
    return handbookIndex === -1
      ? [...navLinks, booksNav]
      : [...navLinks.slice(0, handbookIndex), booksNav, ...navLinks.slice(handbookIndex)];
  }

  return navLinks;
}

function RootLayoutComponent() {
  const { loggedInUser, actingUser, loading: isLoadingAuthState } = useContext(AuthContext);
  const matchRoute = useMatchRoute();
  const logError = useErrorLogger();
  const menuNavLinks = getMenuNavLinks(actingUser);
  const isAdmin = actingUser ? isAirAdmin(actingUser) : false;
  const userIsStudent = actingUser ? isStudent(actingUser) : false;
  const userIsSchoolAdmin = actingUser ? isSchoolAdmin(actingUser) && !isAdmin : false;
  const isCoachUser = actingUser ? isCoach(actingUser) && !userIsSchoolAdmin : false;
  // Students and teachers use a hamburger drawer below `md` so 200% zoom (e.g. 1280×720)
  // still leaves a usable content pane. Collapsible desktop sidebar stays Admin / School Admin / Teacher.
  const usesMobileNavDrawer = isCoachUser || userIsStudent;
  const canCollapseSidebar =
    !!actingUser && !userIsStudent && (isAirAdmin(actingUser) || isSchoolAdmin(actingUser) || isCoach(actingUser));

  // Separate keys per portal — school admin defaults collapsed (incl. when impersonating).
  const [schoolAdminSidebarCollapsed, setSchoolAdminSidebarCollapsed] = useLocalStorage(
    SIDEBAR_COLLAPSED_SCHOOL_ADMIN_KEY,
    true,
  );
  const [adminTeacherSidebarCollapsed, setAdminTeacherSidebarCollapsed] = useLocalStorage(
    SIDEBAR_COLLAPSED_ADMIN_TEACHER_KEY,
    false,
  );
  const sidebarCollapsed =
    canCollapseSidebar && (userIsSchoolAdmin ? schoolAdminSidebarCollapsed : adminTeacherSidebarCollapsed);
  const sidebarWidth = sidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isLoadingAuthState || !loggedInUser) {
      return;
    }
    document.getElementById('main-content')?.focus({ preventScroll: true });
  }, [location.pathname, isLoadingAuthState, loggedInUser?.id]);

  const matchedNavLinks: NavLinkDefinition[] = menuNavLinks.map((navLink) => {
    if ('linkProps' in navLink) {
      return {
        ...navLink,
        fuzzyMatched: !!matchRoute({ to: navLink.linkProps.to, params: navLink.linkProps.params, fuzzy: true }),
        exactMatched: !!matchRoute({ to: navLink.linkProps.to, params: navLink.linkProps.params }),
        children: (navLink.children || EmptyArray).map((child) => ({
          ...child,
          fuzzyMatched: !!matchRoute({ to: child.linkProps.to, params: child.linkProps.params, fuzzy: true }),
          exactMatched: !!matchRoute({ to: child.linkProps.to, params: child.linkProps.params }),
        })),
      };
    }
    return navLink;
  });

  if (isLoadingAuthState) {
    return <LoadingPage />;
  }

  if (!loggedInUser) {
    const redirect = window.location.pathname;

    if (redirect !== '/') {
      return <Navigate to="/login" search={{ redirect }} replace />;
    }

    return <Navigate to="/login" />;
  }

  const layoutStyle = {
    '--sidebar-width': sidebarWidth,
  } as CSSProperties;

  return (
    <main className={cn({ 'flex flex-col h-screen md:block': usesMobileNavDrawer })} style={layoutStyle}>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:rounded-sm focus:bg-background focus:px-3 focus:py-2 focus:text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        Skip to main content
      </a>
      {usesMobileNavDrawer && <MobileHeader userIsStudent={userIsStudent} onOpen={() => setMobileNavOpen(true)} />}
      <div
        className={cn(
          'items-start md:grid md:grid-cols-[var(--sidebar-width)_minmax(0,1fr)] px-0 transition-[grid-template-columns] duration-200 ease-in-out',
          { 'flex-1 min-h-0': usesMobileNavDrawer },
        )}
      >
        <aside
          className={cn(
            'justify-between items-stretch flex-col h-screen max-h-screen border-r border-border transition-[width] duration-200 ease-in-out overflow-hidden',
            usesMobileNavDrawer ? 'hidden md:flex' : 'flex',
            { 'bg-[#eb404a]': userIsStudent },
          )}
          style={{ width: sidebarWidth }}
        >
          <SidebarContent
            matchedNavLinks={matchedNavLinks}
            loggedInUser={loggedInUser}
            actingUser={actingUser}
            userIsStudent={userIsStudent}
            isAdmin={isAdmin}
            isSchoolAdmin={userIsSchoolAdmin}
            collapsed={sidebarCollapsed}
            onToggleCollapsed={
              canCollapseSidebar
                ? () => {
                    if (userIsSchoolAdmin) {
                      setSchoolAdminSidebarCollapsed(!sidebarCollapsed);
                    } else {
                      setAdminTeacherSidebarCollapsed(!sidebarCollapsed);
                    }
                  }
                : undefined
            }
          />
        </aside>
        {usesMobileNavDrawer && (
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetContent
              side="left"
              hideCloseButton
              className={cn(
                'p-0 w-[15rem] sm:max-w-[15rem] flex flex-col justify-between md:hidden border-r border-border',
                { 'bg-[#eb404a]': userIsStudent },
              )}
            >
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <SidebarContent
                matchedNavLinks={matchedNavLinks}
                loggedInUser={loggedInUser}
                actingUser={actingUser}
                userIsStudent={userIsStudent}
                isAdmin={isAdmin}
                isSchoolAdmin={userIsSchoolAdmin}
                onNavigate={() => setMobileNavOpen(false)}
                headerSlot={
                  <SheetClose asChild>
                    <Button
                      variant="outline"
                      aria-label="Close menu"
                      data-testid={NAV.mobileMenuClose}
                      className={cn('rounded p-2 text-foreground transition-colors hover:bg-accent ', {
                        'bg-air-primary text-white': userIsStudent,
                      })}
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </Button>
                  </SheetClose>
                }
              />
            </SheetContent>
          </Sheet>
        )}
        <GeneralErrorBoundary
          onError={(e) => {
            // Log error to Sentry
            logError(e, {
              component: 'layout',
              action: 'session-load',
              userId: actingUser?.id,
            });

            return {
              allowRetry: true,
              errorHeader: 'Failed to load session. Please try again.',
            };
          }}
        >
          <div
            id="main-content"
            tabIndex={-1}
            className={cn(
              'relative min-w-0 px-3 md:px-4 lg:px-6 overflow-auto bg-[#F7F7F9] flex flex-col outline-none',
              usesMobileNavDrawer ? 'flex-1 min-h-0 md:h-screen' : 'h-screen',
            )}
          >
            <Outlet />
          </div>
        </GeneralErrorBoundary>
      </div>
    </main>
  );
}
